<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div class="max-tool">
      <p v-if="!specId" class="text-muted">{{ _('Choose a tool in the component settings.') }}</p>
      <p v-else-if="loading && !item" class="text-muted"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
      <p v-else-if="loadingFailed" class="text-muted">{{ _('This item is no longer available.') }}</p>
      <div v-else-if="item && blockedReason" class="alert alert-info">{{ blockedReason }}</div>
      <template v-else-if="item">
        <div v-if="trial" class="alert alert-warning">
          {{ _('You are trying out this tool before approving it. Every change asks for confirmation first; once the tool is approved, changes are saved at once.') }}
        </div>
        <MaxInterface ref="tool" :key="item.spec_hash" :spec="item.spec" :host="host" :labels="maxLabels" :time-zone="timeZone" :user="maxUser" :image-url="imageUrl">
          <template #actions>
            <button type="button" class="max-action" :aria-label="_('Refresh')" :title="_('Refresh')" v-on:click="$refs.tool && $refs.tool.reload()">
              <i class="fa fa-fw fa-rotate" aria-hidden="true"></i>
            </button>
          </template>
        </MaxInterface>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import components from '~/lib/components'
import maxlabels from '~/lib/maxlabels'
import maxspecs, { maxWindowId } from '~/lib/maxspecs'
import { readsAffectedBy, writtenType } from '#max-core'

/** The same error again within this time does not stack another toast */
const ERROR_REPEAT_MS = 3000

const MaxOperationConfirm = defineAsyncComponent(() => import('~/components/modals/MaxOperationConfirm'))

const NAVIGATE = {
  product: { component: 'ProductDetails', key: 'productId' },
  order: { component: 'OrderDetails', key: 'orderId' },
  customer: { component: 'CustomerDetails', key: 'customerId' },
  resource: { component: 'ResourceDetails', key: 'resourceId' },
  reservation: { component: 'ReservationDetails', key: 'reservationId' },
  bill: { component: 'InvoiceDetails', key: 'invoiceId' },
  category: { component: 'CategoryDetails', key: 'categoryId' },
  issue: { component: 'IssueDetails', key: 'issueId' },
  discount: { component: 'DiscountDetails', key: 'discountId' },
  content: { component: 'ContentDetails', key: 'contentId' },
  show: { component: 'Showtimes', key: 'showId' },
  billingcustomer: { component: 'BillingCustomerDetails', key: 'billingCustomerId' },
  collection: { component: 'CollectionDetails', key: 'collectionId' },
  group: { component: 'GroupDetails', key: 'groupId' }
}

const PRINT_PATHS = {
  receipt: 'receipts',
  invoice: 'bills',
  transfer: 'transfers',
  delivery: 'orders',
  refund: 'refunds'
}

export default {
  data () {
    return {
      item: null
    }
  },
  mixins: [components, maxlabels, maxspecs],
  computed: {
    config () {
      if (this.component && this.component.initData && this.component.initData.specId) {
        return this.component.initData
      }
      try {
        return this.component && this.component.data ? JSON.parse(this.component.data) : {}
      } catch (e) {
        return {}
      }
    },
    specId () {
      return parseInt(this.config.specId, 10) || null
    },
    blockedReason () {
      if (!this.item) {
        return ''
      }
      if (this.item.kind !== 'interface') {
        return this._('This definition is not a tool.')
      }
      if (!this.item.valid) {
        return this._('The stored definition is no longer valid and will not run:') + ' ' + (this.item.issues[0] ? this.item.issues[0].message : '')
      }
      if (this.trial) {
        return ''
      }
      if (!this.item.approved || this.item.status !== 'active') {
        return this._('This tool must be approved and active before it can be used.')
      }
      if (this.item.analysis && this.userLevel < this.item.analysis.minLevel) {
        return this._('You do not have access to this tool.')
      }
      return ''
    },
    trial () {
      return !!this.item && this.item.status === 'draft' && this.item.can_approve
    },
    host () {
      return {
        call: (name, input, options) => this.callOperation(name, input, options),
        navigate: (to, id) => this.navigate(to, id),
        toast: (message, intent) => this.toast(message, intent),
        print: (kind, id) => this.print(kind, id),
        copy: text => this.copyText(text)
      }
    },
    timeZone () {
      return (this.$store && this.$store.getters.timezone) || 'Europe/Helsinki'
    },
    /** The signed-in user for the tool's user scope: id and name only, for display, never access control */
    maxUser () {
      const user = this.$store && this.$store.getters.userData
      if (!user || !user.id) {
        return null
      }
      const id = Number(user.id)
      return { id: Number.isFinite(id) ? id : String(user.id), name: user.name || '' }
    }
  },
  watch: {
    specId (id) {
      if (id && (!this.item || this.item.id !== id)) {
        this.item = null
        this.load()
      }
    }
  },
  methods: {
    async load () {
      this.loading = true
      this.loadingFailed = false
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/maxspecs/' + this.specId + '.json?apikey=' + this.apiKey)
        this.item = response.data
      } catch (e) {
        this.item = null
        this.loadingFailed = true
      }
      this.loading = false
    },
    operationError (e) {
      const status = e && e.response ? e.response.status : 0
      const code = status === 403 ? 'forbidden' : status === 429 ? 'rate_limited' : status === 400 ? 'invalid_input' : 'failed'
      // The tool backs off this many seconds when the backend says so, else 30 s (johku-max runtime)
      const retryAfter = code === 'rate_limited' && e.response.headers ? Number(e.response.headers['retry-after']) : NaN
      return Number.isFinite(retryAfter) && retryAfter > 0
        ? { code, message: this.getErrorMessage(e), retryAfter }
        : { code, message: this.getErrorMessage(e) }
    },
    async post (name, input, token, background) {
      try {
        const response = await axios.post(this.baseDomain + this.shopId + '/maxoperations.json?apikey=' + this.apiKey, {
          spec_id: this.item.id,
          name,
          input,
          token: token || undefined,
          // A background rerun of on.load: the backend counts it apart and caps it
          background: background || undefined,
          origin: maxWindowId
        })
        return response.data
      } catch (e) {
        throw this.operationError(e)
      }
    },
    async callOperation (name, input, options) {
      if (options && options.background) {
        // Reads only (the runtime skips the rest); failures stay quiet in the runtime
        return this.post(name, input, null, true)
      }
      const result = await this.post(name, input)
      if (result && result.url) {
        const path = String(result.url).replace(/^\//, '')
        window.open(this.baseDomain + this.shopId + '/' + path + (path.includes('?') ? '&' : '?') + 'apikey=' + this.apiKey)
        return result
      }
      if (!result || (!result.confirm && !result.changes)) {
        return result
      }
      if (!result.confirm) {
        this.reportSaved(name, result)
        this.announceChange(name, input, result)
        return result
      }
      const facts = result.confirm
      if (!result.token) {
        this.reportSaved(name, { changes: [], ineligible: facts.ineligible || [] })
        return { changes: [], unchanged: facts.unchanged, missing: facts.missing, ineligible: facts.ineligible || [] }
      }
      const confirmed = await this.askConfirmation(facts)
      if (!confirmed) {
        throw { code: 'cancelled', message: '' }
      }
      const done = await this.post(name, input, result.token)
      this.reportSaved(name, done)
      this.announceChange(name, input, done)
      return done
    },
    /**
     * Tells every other open Max tool in this POS tab that records changed, so each reloads
     * what it read when it opened (maxDataChanged). types: the catalog record types written,
     * e.g. reservations; a metadata write names the records' type.
     */
    announceChange (name, input, done) {
      if (!done || !Array.isArray(done.changes) || !done.changes.length) {
        return
      }
      const ref = this.item.spec.operations && this.item.spec.operations[name]
      const operation = ref && ref.operation ? String(ref.operation) : ''
      const type = writtenType(operation, input)
      this.EventBus.$emit('maxDataChanged', {
        shopId: this.shopId,
        specId: this.item.id,
        origin: this.staticComponentId,
        types: type ? [type] : []
      })
    },
    /**
     * Records changed in this window or elsewhere (socket): reload in the background when the
     * tool's on.load reads those record types. The tool waits a moment so several changes
     * share one reload, runs at most once per 30 s and not while the user is working in it.
     */
    onDataChanged (message) {
      if (!message || message.shopId !== this.shopId || message.origin === this.staticComponentId || !this.item || this.blockedReason) {
        return
      }
      if (!this.item.spec || !readsAffectedBy(this.item.spec, message.types)) {
        return
      }
      if (this.$refs.tool && this.$refs.tool.refreshInBackground) {
        this.$refs.tool.refreshInBackground()
      }
    },
    reportSaved (name, done) {
      const ineligible = done.ineligible || []
      if (ineligible.length) {
        const ref = this.item.spec.operations && this.item.spec.operations[name]
        const text = (ref && this.maxOperationIneligible[ref.operation]) || this._('%s cannot be changed this way and are skipped.')
        this.$toast.warning(text.replace('%s', ineligible.length), { autoClose: 8000 })
      }
      if (done.changes.length) {
        this.showNotification(this._('%s changes saved.').replace('%s', done.changes.length))
      } else if (!ineligible.length) {
        this.showNotification(this._('Nothing to change.'))
      }
    },
    askConfirmation (facts) {
      return new Promise(resolve => {
        let answered = false
        const answer = (ok) => {
          if (!answered) {
            answered = true
            resolve(ok)
          }
        }
        this.showModal('maxoperationconfirm', MaxOperationConfirm, {
          props: {
            confirm: facts,
            interfaceTitle: this.item.title
          },
          events: {
            confirm: () => answer(true)
          },
          handlers: {
            closed: () => answer(false)
          },
          width: 640
        })
      })
    },
    async copyText (text) {
      try {
        await navigator.clipboard.writeText(text)
        return true
      } catch (e) {
        return false
      }
    },
    imageUrl (source, id) {
      const n = parseInt(id, 10)
      if (!n) return null
      // The product's main image or a shop file, as the POS product lists load them
      return source === 'product'
        ? this.baseDomain + this.shopId + '/products/' + n + '/files.jpg'
        : this.baseDomain + this.shopId + '/files/' + n + '.jpg'
    },
    navigate (to, id) {
      if (to === 'message') {
        const type = id === null || id === undefined ? '' : String(id)
        if (!type) return
        this.EventBus.$emit('showDetailsColumn', {
          shopId: this.shopId,
          component: 'MessageDetails',
          title: this._('Message'),
          width: 6,
          data: { type, trail: this.componentTrail }
        })
        return
      }
      const target = NAVIGATE[to]
      const entityId = parseInt(id, 10)
      if (!target || !entityId) {
        return
      }
      const titles = {
        product: this._('Product'),
        order: this._('Order'),
        customer: this._('Customer'),
        resource: this._('Resource'),
        reservation: this._('Reservation'),
        bill: this._('Invoice'),
        category: this._('Category'),
        issue: this._('Issue'),
        discount: this._('Discount'),
        content: this._('Content'),
        show: this._('Show'),
        billingcustomer: this._('Billing customer'),
        collection: this._('Collection'),
        group: this._('Group')
      }
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: target.component,
        title: (titles[to] || to) + ' ' + entityId,
        width: 6,
        data: { [target.key]: entityId, trail: this.componentTrail }
      })
    },
    print (kind, id) {
      const folder = PRINT_PATHS[kind]
      const entityId = parseInt(id, 10)
      if (!folder || !entityId) {
        return
      }
      window.open(this.baseDomain + this.shopId + '/' + folder + '/' + entityId + '.pdf')
    },
    toast (message, intent) {
      if (!message) {
        return
      }
      if (intent === 'negative') {
        // Several calls failing the same way (e.g. too many requests) show one toast, not a stack
        const text = this.parseToastMessage(message)
        const id = 'max-error:' + text
        const now = Date.now()
        this.errorShown = this.errorShown || {}
        if (this.$toast.isActive(id) || now - (this.errorShown[id] || 0) < ERROR_REPEAT_MS) {
          return
        }
        this.errorShown[id] = now
        return this.$toast.error(text, { toastId: id })
      }
      this.showCustomNotification(message)
    }
  },
  created () {
    if (this.specId) {
      this.load()
    }
  },
  mounted () {
    if (!this.events) {
      this.events = {
        maxSpecUpdate: (message) => {
          if (message && this.item && message.id === this.item.id) {
            this.load()
          }
        },
        maxDataChanged: (message) => this.onDataChanged(message)
      }
    }
    this.enableEventBus()
  }
}
</script>

<style scoped>
.max-tool > .alert {
  margin-top: 15px;
}
</style>
